import { z } from "zod";

/**
 * Orphan-drafts recovery contract — the shapes exchanged between the
 * admin-ui and the orchestrator's `/api/jobs/:id/orphan-drafts` (GET)
 * and `/api/jobs/:id/clear-orphan-drafts` (POST) endpoints.
 *
 * Background: when a curator authors `tools_draft` rows but the job
 * later reaches a terminal status without those rows being cleared
 * (HITL deadline expired, post-rollup clear silently warn-logged, or
 * the curator failed mid-author), the rows remain in the Sheet and
 * block the next curator dispatch via the `tools_draft_non_empty`
 * pre-flight guard. These endpoints expose an operator-driven
 * recovery action on JobDetail.
 *
 * The GET is a read-only probe driving banner visibility. The POST is
 * idempotent: it intersects this job's `draft_authorship.draft_id`
 * rows with the live `tools_draft` Sheet rows and clears the
 * survivors. A second call against an already-cleared job sees an
 * empty intersection and returns `cleared: 0` with no audit row.
 *
 * Decision rationale lives in issue #158 (m1-5-5 gate-run finding).
 */

/**
 * GET `/api/jobs/:id/orphan-drafts` response. The SPA reads `canClear`
 * to gate the banner; `orphanSlugs` populates the confirmation modal's
 * row list. `jobStatus` is echoed so the SPA doesn't need a separate
 * job fetch to decide what copy to render.
 *
 * `canClear` is `true` iff the job is in a terminal status
 * (`expired | completed | cancelled | failed`) AND `orphanSlugs` is
 * non-empty. The orchestrator computes this — the SPA does NOT
 * re-derive it from `jobStatus`, so the predicate stays in one place.
 *
 * `unratedSlugs` is the subset of `orphanSlugs` whose `tools_draft`
 * row has an empty `quality_score` — i.e. rows the validator would
 * still score. Populated so the SPA can gate the "Send drafts through
 * validation" affordance on JobDetail (issue #641) without a second
 * server round-trip: a targeted-mode curator that skipped the
 * validator leaves unrated drafts, and the operator's next step is
 * to dispatch the validator catch-up. Empty when every orphan row
 * has already been scored (autonomous-mode leftovers, or targeted-
 * mode drafts a subsequent validator catch-up already handled).
 */
export const orphanDraftsResponseSchema = z.object({
  jobStatus: z.string(),
  orphanSlugs: z.array(z.string().min(1)),
  unratedSlugs: z.array(z.string().min(1)),
  canClear: z.boolean(),
});

export type OrphanDraftsResponse = z.infer<typeof orphanDraftsResponseSchema>;

/**
 * POST `/api/jobs/:id/clear-orphan-drafts` response. `cleared` is the
 * number of Sheet rows actually deleted (matches `slugs.length`);
 * `slugs` is the exact list, so the SPA can render a confirmation
 * toast without a follow-up GET. Both fields are zero on the
 * idempotent-no-op path.
 */
export const clearOrphanDraftsResponseSchema = z.object({
  cleared: z.number().int().nonnegative(),
  slugs: z.array(z.string().min(1)),
});

export type ClearOrphanDraftsResponse = z.infer<
  typeof clearOrphanDraftsResponseSchema
>;
